import { useTranslation } from '@invision/core';
import { ThemeProvider, useTheme } from '@mui/material';
import React, { PropsWithChildren } from 'react';
import { Button } from '../../inputs/Button';
import { error, info, success, warning } from '../../themes';
import { adaptIntentDialogTheme } from '../../themes/adaptIntentDialogTheme';
import { ErrorIcon } from '../../utility/Icon/ErrorIcon';
import { SuccessIcon } from '../../utility/Icon/SuccessIcon';
import { TruncatedText } from '../../utility/TruncatedText';
import { Dialog, DialogActions, DialogContent } from '../Dialog';
import { Box } from '../Box';

export interface ConfirmationDialogProps {
  isOpen: boolean;
  close: () => void;
  /** Replaces the default primary + cancel action row (full custom footer). */
  dialogActions?: React.ReactNode;
  /** Theme palette for header icon + button chrome: maps to internal `warning` | `error` | `success` | `info`. */
  type?: 'warning' | 'error' | 'success' | 'info';
  title: React.ReactNode;
  /** Optional header icon; clones with size/margin when provided. */
  icon?: React.ReactElement;
  confirmButtonText?: string;
  /** Cancel label; defaults to translated “Cancel”. */
  cancelButtonText?: string;
  hideCancelButton?: boolean;
  /** Called when the user confirms (primary button). */
  onSubmit?: () => void;
}

/**
 * Modal confirmation with themed header icon, title, body (`children`), and default Cancel / Confirm actions.
 */
export function ConfirmationDialog({
  isOpen,
  close,
  title,
  icon,
  children,
  confirmButtonText,
  cancelButtonText,
  dialogActions,
  hideCancelButton,
  onSubmit,
  type
}: PropsWithChildren<ConfirmationDialogProps>) {
  const { t } = useTranslation();
  const parentTheme = useTheme();
  let theme = warning;
  const HeaderIcon = type === 'success' ? SuccessIcon : ErrorIcon;
  const dialogTitle = (
    <Box
      style={{
        display: 'inline-flex',
        alignItems: 'center'
      }}
    >
      {icon ? (
        React.cloneElement(icon as React.ReactElement<{ sx?: object }>, {
          sx: {
            fontSize: '1.125rem',
            mr: 0.75,
            ...(icon as React.ReactElement<{ sx?: object }>).props.sx
          }
        })
      ) : (
        <HeaderIcon
          sx={{
            fontSize: '1rem',
            padding: '0px 6px 0px 0px'
          }}
        />
      )}
      <TruncatedText>{title}</TruncatedText>
    </Box>
  );

  if (type === 'error') {
    theme = error;
  } else if (type === 'success') {
    theme = success;
  } else if (type === 'info') {
    theme = info;
  }

  return (
    <ThemeProvider theme={adaptIntentDialogTheme(theme, parentTheme)}>
      <Dialog
        open={isOpen}
        title={dialogTitle}
        onClose={close}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent
          sx={{
            paddingTop: '1.5rem',
            paddingBottom: '1.5rem',
            lineHeight: '1.5'
          }}
        >
          {children}
        </DialogContent>
        <DialogActions>
          {dialogActions || (
            <>
              {!hideCancelButton && (
                <Button
                  onClick={close}
                  data-cy="confirmationDialogAction-cancel"
                >
                  {cancelButtonText || t('Cancel')}
                </Button>
              )}
              <Button
                color="primary"
                data-cy="confirmationDialogAction-confirm"
                onClick={onSubmit}
              >
                {confirmButtonText}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
